import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RoleSkillSetup, RoleSkillSetupParams } from '../../interfaces/career-setup/role-skill-setup.interface';
import { RoleSkillSetupService } from './role-skill-setup.service';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { environment } from '../../../../environments/environment.dev';
import { convertToHttpParams, CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { PageResponse } from '../../interfaces/page-response.interface';

@Injectable({
  providedIn: 'root'
})
export class RoleSkillSetupApiService extends RoleSkillSetupService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/setup-data/career-setup/skills`;

  getRoleSkills(params?: RoleSkillSetupParams): Observable<PageResponse<RoleSkillSetup>> {
    const httpParams = convertToHttpParams(params || {});
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<RoleSkillSetup>>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 0, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<RoleSkillSetup>> {
    const httpParams = convertToHttpParams(params);
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<RoleSkillSetup>>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 0, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override find(id: string): Observable<RoleSkillSetup> {
    return this.wrapRequest(
      this.http.get<ApiResponse<RoleSkillSetup>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override create(data: Partial<RoleSkillSetup>): Observable<RoleSkillSetup> {
    return this.wrapRequest(
      this.http.post<ApiResponse<RoleSkillSetup>>(this.baseUrl, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override update(id: string, data: Partial<RoleSkillSetup>): Observable<RoleSkillSetup> {
    return this.wrapRequest(
      this.http.put<ApiResponse<RoleSkillSetup>>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override updateStatus(id: string, isActive: boolean): Observable<RoleSkillSetup> {
    return this.wrapRequest(
      this.http.patch<ApiResponse<RoleSkillSetup>>(`${this.baseUrl}/${id}/status`, null, {
        headers: this.headers,
        params: { isActive: isActive.toString() }
      }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override delete(id: string): Observable<void> {
    return this.wrapRequest(
      this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(() => undefined)
      ),
      false,
      false
    );
  }
}
